<script setup lang="ts">
import { invariant, nonNullable } from '@/shared/utils/typeAssertions'
import { useStorage } from '@vueuse/core'
import { computed, ref, watch, type Ref } from 'vue'

/**
 * NOTE: This component was created to consolidate different
 * resizable panel scenarios in the application.
 *
 * Because of this, the property definitions are the bare
 * minimum required to support the use cases we had at the time.
 *
 * Specifically, minWidth for panels was specified in px and max in vw,
 * so these are the two property types the component optionally supports.
 *
 * We can extend this further if we need to,
 * by adding an optional prop to this type, and then extending `isOutOfBounds`
 */
type PanelConfig = {
  id: string
  minWidthPx?: number
  maxWidthVw?: number
  defaultPercentage?: number
  show?: boolean
}

const props = defineProps<{ panels: PanelConfig[]; storageKey?: string }>()

type PanelSizes = Record<string, number>

const initialPanelSizes = getInitialPanelSizes()

const panelRefs = ref<Record<string, HTMLElement | null>>({})
const panelSizes: Ref<PanelSizes> = ref(initialPanelSizes)

const storageRef = computed(() => {
  if (!props.storageKey) return null
  return useStorage(props.storageKey, initialPanelSizes, undefined, { writeDefaults: false })
})

/**
 * We might or might not have a storage key, so we need to watch the storageRef
 * and update the panelSizes accordingly.
 * If there is a storage key, we will swap the ref to the storageRef and sync through it.
 */
watch(
  storageRef,
  (nextStorage, prevStorage) => {
    if (nextStorage) {
      panelSizes.value = nextStorage.value
    } else if (!nextStorage && prevStorage) {
      // If storage is removed, disconnect panel sizes from the previously used storage
      panelSizes.value = getInitialPanelSizes()
    }
  },
  { immediate: true },
)

function getVisiblePanelWidths(): [PanelSizes, number] {
  const panelWidths: Record<string, number> = {}
  let totalWidth = 0
  for (const panel of props.panels) {
    const panelEl = panelRefs.value[panel.id]
    invariant(panelEl instanceof HTMLElement)
    panelWidths[panel.id] = panelEl.clientWidth
    totalWidth += panelEl.clientWidth
  }
  return [panelWidths, totalWidth]
}

const isDragging = ref(false)

const toVw = (px: number) => (px / document.documentElement.clientWidth) * 100

const getOutOfBoundsAmount = (width: number, config: PanelConfig) => {
  if (config.minWidthPx && width < config.minWidthPx) {
    return config.minWidthPx - width
  }

  if (config.maxWidthVw && toVw(width) > config.maxWidthVw) {
    return toVw(width) - config.maxWidthVw
  }

  return 0
}

function onDragHandleMouseDown(panelIndex: number, e: MouseEvent) {
  isDragging.value = true

  // Block interactions with the document while resizing (clicking, hover, selection)
  document.body.setAttribute('inert', 'true')

  const [panelWidthsPx, totalWidthPx] = getVisiblePanelWidths()

  const leftPanelConfig = props.panels.at(panelIndex)
  const rightPanelConfig = props.panels.slice(panelIndex + 1).find((p) => p.show)
  invariant(leftPanelConfig && rightPanelConfig)

  const leftPanel = panelRefs.value[leftPanelConfig.id]
  const rightPanel = panelRefs.value[rightPanelConfig.id]
  invariant(leftPanel && rightPanel)

  const leftPanelWidth = nonNullable(panelWidthsPx[leftPanelConfig.id])
  const rightPanelWidth = nonNullable(panelWidthsPx[rightPanelConfig.id])

  const startingScreenX = e.screenX

  const handleMouseMove = ({ screenX }: MouseEvent) => {
    const delta = screenX - startingScreenX

    const nextLeftWidth = leftPanelWidth + delta
    if (
      getOutOfBoundsAmount(leftPanelWidth, leftPanelConfig) <
      getOutOfBoundsAmount(nextLeftWidth, leftPanelConfig)
    ) {
      return
    }

    const nextRightWidth = rightPanelWidth - delta
    if (
      getOutOfBoundsAmount(rightPanelWidth, rightPanelConfig) <
      getOutOfBoundsAmount(nextRightWidth, rightPanelConfig)
    ) {
      return
    }

    leftPanel.style.width = nextLeftWidth + 'px'
    rightPanel.style.width = nextRightWidth + 'px'
  }

  const handleMouseUp = () => {
    const leftPercentage = (100 * leftPanel.clientWidth) / totalWidthPx
    const rightPercentage = (100 * rightPanel.clientWidth) / totalWidthPx

    panelSizes.value[leftPanelConfig.id] = leftPercentage
    panelSizes.value[rightPanelConfig.id] = rightPercentage

    leftPanel.style.width = 'unset'
    rightPanel.style.width = 'unset'

    document.body.removeAttribute('inert')
    document.removeEventListener('mousemove', handleMouseMove)
    isDragging.value = false
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp, { once: true })
}

/**
 * Initialize the default sizes for the panels.
 *
 * This is done by first assigning the default percentages where specified,
 * and then distributing the remaining space evenly among the panels that don't have a default percentage.
 */
function getInitialPanelSizes(): PanelSizes {
  const sizes: PanelSizes = {}

  // First pass: assign default percentages where specified
  let remaining = 100
  let unassignedPanels = 0

  for (const panel of props.panels) {
    if (panel.defaultPercentage) {
      sizes[panel.id] = panel.defaultPercentage
      remaining -= panel.defaultPercentage
    } else {
      unassignedPanels++
    }
  }

  // Second pass: distribute remaining space evenly
  const defaultSize = unassignedPanels > 0 ? remaining / unassignedPanels : 0
  for (const panel of props.panels) {
    if (!panel.defaultPercentage) {
      sizes[panel.id] = defaultSize
    }
  }

  return sizes
}

const adjustedPanels = computed(() => {
  const totalWidthOfNonHiddenNonPrimaryPanels = props.panels
    .slice(1)
    .reduce((acc, panel) => (panel.show ? acc + (panelSizes.value[panel.id] || 0) : acc), 0)

  return props.panels.map((panel, i) => {
    const width = i === 0 ? 100 - totalWidthOfNonHiddenNonPrimaryPanels : panelSizes.value[panel.id]
    return {
      ...panel,
      width: width + '%',
    }
  })
})
</script>

<template>
  <div class="flex size-full">
    <div
      v-for="(panel, i) in adjustedPanels"
      :ref="(el, i) => (panelRefs[panel.id] = el as HTMLElement | null)"
      :key="panel.id"
      :style="{ width: panel.show ? panel.width : '0%' }"
      class="flex size-full min-w-0 shrink grow overflow-hidden duration-200 ease-in-out-quint"
      :class="[isDragging ? 'transition-none' : 'transition-all']"
      :data-test="`resizable-panel-${panel.id}`"
    >
      <!-- Panel -->
      <Transition
        enter-active-class="transition-all duration-200"
        enter-from-class="opacity-0 w-0 scale-90"
        enter-to-class="opacity-100 w-full scale-100"
        leave-active-class="transition-all duration-200"
        leave-from-class="opacity-100 w-full scale-100"
        leave-to-class="opacity-0 w-0 scale-90"
      >
        <div
          v-if="panel.show"
          class="relative h-full grow overflow-auto"
          :style="{
            width: `calc(${panel.width} - 1px)`,
            minWidth: panel.show ? (panel.minWidthPx ?? 0 + 'px') : '0px',
          }"
        >
          <slot :name="panel.id" />
        </div>
      </Transition>
      <!-- Handle -->
      <div
        v-if="i < panels.length - 1"
        class="relative h-full w-px shrink-0 cursor-col-resize border-l border-border-subtle"
        @mousedown.stop="onDragHandleMouseDown(i, $event)"
      >
        <!--
        Odd margin and width because the handle is 1px wide and this should be spread
        out, less so on the left so that the panel to the left can still be
        scrolled by dragging the scrollbar
      -->
        <div class="absolute z-1 ml-[-3px] h-full w-[11px]" />
      </div>
    </div>
  </div>
</template>
